import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LogIn } from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/annonces", label: "Annonces" },
  { to: "/recherche-par-usage", label: "Par usage" },
  { to: "/proprietaires", label: "Propriétaires" },
  { to: "/exploitants", label: "Exploitants" },
  { to: "/quel-usage", label: "Ressources" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      <header className="sticky top-0 z-50 bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-900">
        <div className="px-6 md:px-12 lg:px-16 h-20 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
            <img
              src="/logo.png"
              alt="R-1.immo"
              className="h-10 w-auto mix-blend-screen"
            />
            <div className="flex flex-col leading-none">
              <span className="font-display text-lg font-medium tracking-tight">R-1.IMMO</span>
              <span className="label-mono text-[11px] tracking-[0.25em] mt-1">m² invisibles · fr</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "")}`}
                className={`text-base tracking-tight transition-colors ${
                  pathname === n.to ? "text-amber-500" : "text-zinc-300 hover:text-white"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/deposer"
              data-testid="cta-deposer-header"
              className="px-5 py-2.5 bg-amber-500 text-black text-sm font-medium uppercase tracking-widest hover:bg-amber-400 transition-colors"
            >
              Proposer un R-1
            </Link>
            {user ? (
              <div className="flex items-center gap-4">
                <Link
                  to={user.role === "admin" ? "/admin" : "/dashboard"}
                  data-testid="nav-dashboard"
                  className="text-sm uppercase tracking-widest text-zinc-300 hover:text-white"
                >
                  {user.role === "admin" ? "Admin" : "Dashboard"}
                </Link>
                <button
                  onClick={() => { logout(); nav("/"); }}
                  data-testid="logout-btn"
                  className="text-sm uppercase tracking-widest text-zinc-500 hover:text-white"
                >
                  Sortir
                </button>
              </div>
            ) : (
              <Link to="/login" data-testid="nav-login" aria-label="Connexion" title="Connexion" className="p-2 border border-zinc-800 text-zinc-300 hover:text-amber-500 hover:border-amber-500 transition-colors">
                <LogIn size={18} />
              </Link>
            )}
          </div>

          <button data-testid="mobile-menu-toggle" className="lg:hidden text-zinc-100" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-zinc-900 px-6 py-4 flex flex-col gap-4 bg-zinc-950">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-sm text-zinc-200">
                {n.label}
              </Link>
            ))}
            <Link to="/deposer" onClick={() => setOpen(false)} className="text-sm text-amber-500">
              Proposer un R-1 →
            </Link>
            {user ? (
              <button onClick={() => { logout(); setOpen(false); }} className="text-sm text-zinc-500 text-left">Sortir</button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm text-zinc-300">Connexion</Link>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-black border-t border-zinc-900 px-6 md:px-12 lg:px-16 py-20 mt-24">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <img src="/logo.png" alt="R-1.immo" className="h-14 w-auto mix-blend-screen" />
              <div className="font-display text-2xl">R-1.IMMO</div>
            </div>
            <p className="text-zinc-500 text-sm max-w-md leading-relaxed">
              La marketplace des m² invisibles. Sous-sols, caves, parkings, coffres, réserves — nous connectons les propriétaires de lieux atypiques aux exploitants qui savent les révéler.
            </p>
          </div>
          <div>
            <div className="label-mono mb-4">Plateforme</div>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li><Link to="/annonces" className="hover:text-white">Toutes les annonces</Link></li>
              <li><Link to="/deposer" className="hover:text-white">Déposer une annonce</Link></li>
              <li><Link to="/recherche-par-usage" className="hover:text-white">Recherche par usage</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-mono mb-4">Ressources</div>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li><Link to="/quel-usage" className="hover:text-white">Quel usage ?</Link></li>
              <li><Link to="/a-propos" className="hover:text-white">À propos</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
            </ul>
          </div>
        </div>
        <div className="tech-line my-12" />
        <div className="flex flex-col md:flex-row justify-between text-xs text-zinc-600 label-mono">
          <span>© 2026 R-1.IMMO</span>
          <span>PARIS · LYON · MARSEILLE · BORDEAUX · LILLE</span>
        </div>
      </footer>
    </div>
  );
}
